/**
 * @file PickupPointsMap.tsx
 * @description Карта пунктов выдачи в админке (phase19.12).
 *
 * Виджет СДЭК здесь не подходит — он про выбор пункта покупателем при заказе,
 * а нам нужен просмотр собственной базы. Рисуем Яндекс.Картами тем же ключом,
 * что задан в настройках перевозчика (`yandexMapsApiKey`).
 *
 * Скрипт API грузим лениво и один раз на вкладку: карта нужна только при
 * переключении вида, тянуть её на каждый заход в раздел незачем.
 */

import { Loader2, MapPin } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Alert } from "../ui/alert";
import type { ShopPickupPointDto } from "../../lib/api-shop-carriers";

/** Минимальная часть API Яндекс.Карт, которой мы пользуемся. */
interface YMaps {
  ready: (cb: () => void) => void;
  Map: new (el: HTMLElement, state: Record<string, unknown>) => YMap;
  Placemark: new (
    coords: [number, number],
    props: Record<string, unknown>,
    opts?: Record<string, unknown>,
  ) => YPlacemark;
}
interface YMap {
  geoObjects: {
    add: (obj: YPlacemark) => void;
    removeAll: () => void;
    getBounds: () => [[number, number], [number, number]] | null;
  };
  setBounds: (b: [[number, number], [number, number]], o?: Record<string, unknown>) => void;
  destroy: () => void;
}
interface YPlacemark {
  events: { add: (name: string, cb: () => void) => void };
}

declare global {
  interface Window {
    ymaps?: YMaps;
  }
}

const SCRIPT_ID = "yandex-maps-api";

/** Грузит скрипт API один раз; повторные вызовы ждут тот же промис. */
let loaderPromise: Promise<YMaps> | null = null;

function loadYmaps(apiKey: string): Promise<YMaps> {
  if (window.ymaps) return Promise.resolve(window.ymaps);
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise<YMaps>((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${encodeURIComponent(apiKey)}&lang=ru_RU`;
    script.async = true;
    script.onload = () => {
      if (window.ymaps) resolve(window.ymaps);
      else reject(new Error("Яндекс.Карты загрузились, но API недоступен"));
    };
    script.onerror = () =>
      reject(new Error("Не удалось загрузить Яндекс.Карты — проверьте ключ и домен"));
    document.head.appendChild(script);
  });
  return loaderPromise;
}

interface Props {
  points: ShopPickupPointDto[];
  apiKey: string | null;
  selectedId: string | null;
  onSelect: (point: ShopPickupPointDto) => void;
}

export function PickupPointsMap({ points, apiKey, selectedId, onSelect }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<YMap | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [errorText, setErrorText] = useState<string | null>(null);

  // Инициализация карты.
  useEffect(() => {
    if (!apiKey || !containerRef.current) return;
    let disposed = false;
    setStatus("loading");
    loadYmaps(apiKey)
      .then((ymaps) => {
        ymaps.ready(() => {
          if (disposed || !containerRef.current) return;
          mapRef.current = new ymaps.Map(containerRef.current, {
            center: [55.16, 61.4], // Челябинск — наш домашний регион
            zoom: 10,
            controls: ["zoomControl", "fullscreenControl"],
          });
          setStatus("ready");
        });
      })
      .catch((err: Error) => {
        if (disposed) return;
        setErrorText(err.message);
        setStatus("error");
      });
    return () => {
      disposed = true;
      mapRef.current?.destroy();
      mapRef.current = null;
    };
  }, [apiKey]);

  // Перерисовка меток при смене выборки.
  useEffect(() => {
    const map = mapRef.current;
    const ymaps = window.ymaps;
    if (status !== "ready" || !map || !ymaps) return;

    map.geoObjects.removeAll();
    const withCoords = points.filter((p) => p.lat !== null && p.lng !== null);
    for (const p of withCoords) {
      const mark = new ymaps.Placemark(
        [p.lat as number, p.lng as number],
        {
          balloonContentHeader: `${p.externalCode} · ${p.carrier}`,
          balloonContentBody: `${p.address}<br/>${p.workingHours ?? ""}`,
          hintContent: p.address,
        },
        {
          preset:
            p.id === selectedId ? "islands#redDotIconWithCaption" : "islands#greenDotIcon",
        },
      );
      mark.events.add("click", () => onSelect(p));
      map.geoObjects.add(mark);
    }
    const bounds = map.geoObjects.getBounds();
    if (bounds) map.setBounds(bounds, { checkZoomRange: true, zoomMargin: 40 });
  }, [points, status, selectedId, onSelect]);

  if (!apiKey) {
    return (
      <Alert severity="warning">
        Не задан ключ Яндекс.Карт — карта недоступна. Укажите его в настройках перевозчика,
        раздел «Расчёт доставки».
      </Alert>
    );
  }

  return (
    <div className="relative min-h-[60vh] overflow-hidden rounded-md border">
      <div ref={containerRef} className="h-[60vh] w-full" />
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-surface-lowest/80 text-sm text-on-surface-variant">
          <Loader2 className="size-4 animate-spin" /> Загружаем карту…
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <Alert severity="error">{errorText ?? "Карта недоступна"}</Alert>
        </div>
      )}
      {status === "ready" && points.every((p) => p.lat === null) && (
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-md bg-surface-lowest/90 px-3 py-2 text-xs text-on-surface-variant">
          <MapPin className="size-3.5" /> У пунктов нет координат — обновите кэш ПВЗ
        </div>
      )}
    </div>
  );
}
